<template>
    <v-form ref="form" @submit.prevent="submit">
        <FormTitle class="pa-4 pt-0" dark
            >Musicologie Student Experience Survey
            <template #subtitle
                >Seven questions, multiple choice, no writing if you don’t want
                to. When you're done, we have a <b>special gift</b> for you!
            </template>
        </FormTitle>

        <FormItem required>
            <template #question>
                How likely are you to recommend&nbsp<u>Musicologie</u>&nbspto a
                friend or colleague?
            </template>
            <template #response>
                <NPSOptions
                    v-model="form.recommend_us"
                    :error-messages="errors.recommend_us"
                    :rules="rules_required"
                />
                <v-textarea
                    v-if="form.recommend_us !== null && form.recommend_us < 8"
                    v-model="form.recommend_us_reason"
                    label="What's the reason for this score?"
                    outlined
                    dense
                    hide-details="auto"
                    :rules="rules_text_limit"
                    counter
                    maxlength="255"
                />
                <div
                    v-if="form.recommend_us >= 8"
                    class="soli-grey text-start"
                    style="display: block"
                >
                    Awesome!
                    <a
                        href="https://musicologielessons.com/review"
                        target="_blank"
                        >Go here to review us</a
                    >. It just takes 30 seconds and won't mess up this form.
                </div>
            </template>
        </FormItem>

        <FormItem required>
            <template #question>
                How likely are you to recommend&nbsp<u>your teacher</u
                >{{ `${teacher ? `, ${teacher.name},` : ""}` }} to a friend or
                colleague?
            </template>
            <template #response>
                <NPSOptions
                    v-model="form.recommend_teacher"
                    :error-messages="errors.recommend_teacher"
                    :rules="rules_required"
                />
                <v-textarea
                    v-if="
                        form.recommend_teacher !== null &&
                        form.recommend_teacher < 8
                    "
                    v-model="form.recommend_teacher_reason"
                    label="What's the reason for this score?"
                    outlined
                    dense
                    hide-details="auto"
                    :rules="rules_text_limit"
                    counter
                    maxlength="255"
                />
            </template>
        </FormItem>

        <FormItem required>
            <template #question
                >Where did you first hear about Musicologie?</template
            >
            <template #response>
                <v-radio-group
                    v-model="form.hear_about_us"
                    :rules="rules_required"
                    :errors="errors.hear_about_us"
                    hide-details="auto"
                    class="mt-0"
                >
                    <v-radio
                        v-for="(option, idx) in hearAboutUsOptions"
                        :key="idx"
                        :label="option"
                        :value="option"
                /></v-radio-group>
                <SoliText
                    v-if="
                        form.hear_about_us == 'From another business' ||
                        form.hear_about_us == 'At an event'
                    "
                    v-model="form.hear_about_us_detail"
                    :label="`Which ${
                        form.hear_about_us.includes('business')
                            ? 'business'
                            : 'event'
                    }?`"
                    outlined
                    dense
                    hide-details="auto"
                    class="pt-3"
                    :rules="rules_string_limit"
                    counter
                    maxlength="75" />
                <SoliText
                    v-if="form.hear_about_us == 'Other'"
                    v-model="form.hear_about_us_other"
                    label="Where?"
                    outlined
                    dense
                    hide-details="auto"
                    class="pt-3"
                    :rules="rules_string_limit"
                    counter
                    maxlength="75"
            /></template>
        </FormItem>

        <FormItem required>
            <template #question
                >Which three of the following things are <u>MOST</u> important
                for you in your lessons?</template
            >
            <template #response>
                <v-input
                    :rules="[
                        (val) =>
                            val.length <= 3 || 'Please select only three items',
                    ]"
                    :value="form.most_important"
                    :error-messages="errors.most_important"
                    hide-details="auto"
                >
                    <div class="flex flex-col">
                        <div
                            v-for="(option, idx) in importantOptions"
                            :key="idx"
                        >
                            <v-checkbox
                                v-model="form.most_important"
                                class="mt-0 mb-1"
                                :label="option"
                                :value="option"
                                hide-details="auto"
                            />
                            <SoliText
                                v-if="
                                    form.most_important.includes(
                                        'Achieving a specific goal'
                                    ) &&
                                    idx ==
                                        importantOptions.indexOf(
                                            'Achieving a specific goal'
                                        )
                                "
                                v-model="form.most_important_specific_goal"
                                label="What is that goal?"
                                outlined
                                dense
                                hide-details="auto"
                                class="pt-3"
                                :rules="rules_string_limit"
                                counter
                                maxlength="75"
                            />
                            <SoliText
                                v-if="
                                    form.most_important.includes('Other') &&
                                    idx == importantOptions.indexOf('Other')
                                "
                                v-model="form.most_important_other"
                                label="What is most important?"
                                outlined
                                dense
                                class="pt-3"
                                :error-messages="errors.most_important_other"
                                :rules="rules_string_limit"
                                counter
                                maxlength="75"
                            />
                        </div></div></v-input
            ></template>
        </FormItem>
        <FormItem required
            ><template #question
                >How well are we delivering those top three things to
                you?</template
            ><template #response>
                <NPSOptions
                    v-model="form.are_we_delivering"
                    :error-messages="errors.are_we_delivering"
                    :rules="rules_required"
                    :labels="['Not well', 'Very well']"
                /> </template
        ></FormItem>
        <FormItem required>
            <template #question
                >Which three of the following things are <u>LEAST</u> important
                for you in your lessons?</template
            >
            <template #response>
                <v-input
                    ref="least_important"
                    :rules="[
                        (val) =>
                            val.length <= 3 || 'Please select only three items',
                    ]"
                    :value="form.least_important"
                    :error-messages="errors.least_important"
                    hide-details="auto"
                >
                    <div class="flex flex-col">
                        <div
                            v-for="(option, idx) in importantOptions"
                            :key="idx"
                        >
                            <v-checkbox
                                v-model="form.least_important"
                                class="mt-0 mb-1"
                                :label="option"
                                :value="option"
                                hide-details="auto"
                            />
                            <SoliText
                                v-if="
                                    form.least_important.includes(
                                        'Achieving a specific goal'
                                    ) &&
                                    idx ==
                                        importantOptions.indexOf(
                                            'Achieving a specific goal'
                                        )
                                "
                                v-model="form.least_important_specific_goal"
                                label="What is that goal?"
                                outlined
                                dense
                                hide-details="auto"
                                class="pt-3"
                                :rules="rules_string_limit"
                                counter
                                maxlength="75"
                            />
                            <SoliText
                                v-if="
                                    form.least_important.includes('Other') &&
                                    idx == importantOptions.indexOf('Other')
                                "
                                v-model="form.least_important_other"
                                label="What is least important?"
                                outlined
                                dense
                                hide-details="auto"
                                class="pt-3"
                                :rules="rules_string_limit"
                                counter
                                maxlength="75"
                            />
                        </div></div></v-input></template
        ></FormItem>
        <FormItem
            ><template #question>Anything we could do better?</template
            ><template #response>
                <v-textarea
                    v-model="form.do_better"
                    placeholder="You answer"
                    outlined
                    dense
                    hide-details="auto"
                    :rules="rules_text_limit"
                    counter
                    maxlength="255" /></template
        ></FormItem>
        <FormSubmit
            dark
            type="submit"
            :loading="loading"
            valid
            :errors="errors"
            gradient="gradient-orange"
            icon="send"
        ></FormSubmit>
    </v-form>
</template>

<script>
import globalMoment from "../../Mixins/GlobalMoment";
import FormRules from "../../Mixins/FormRules";
import NPSOptions from "../Form/NPSOptions.vue";
import FormItem from "../Form/FormItem.vue";
import FormTitle from "../Form/FormTitle.vue";
import FormSubmit from "../Form/FormSubmit.vue";

export default {
    mixins: [globalMoment, FormRules],
    components: {
        NPSOptions,
        FormTitle,
        FormItem,
        FormSubmit,
    },
    props: {
        errors: {},
        user: {
            type: Object,
            default: () => {},
        },
        hearAboutUsOptions: {
            type: Array,
            default: () => [],
        },
        importantOptions: {
            type: Array,
            default: () => [],
        },
        teacher: {
            type: Object,
            default: null,
        },
        locationUserId: {
            type: Number,
            default: null,
        },
        source: {
            type: String,
            default: null,
        },
    },
    data() {
        return {
            form: this.$inertia.form({
                recommend_us: null,
                recommend_us_reason: null,
                recommend_teacher: null,
                recommend_teacher_reason: null,
                hear_about_us: null,
                hear_about_us_detail: null,
                hear_about_us_other: null,
                most_important: [],
                most_important_specific_goal: null,
                most_important_other: null,
                are_we_delivering: null,
                least_important: [],
                least_important_specific_goal: null,
                least_important_other: null,
                do_better: null,
                referral_first_name: null,
                referral_last_name: null,
                referral_email: null,
                teacher_id: this.teacher?.id ?? null,
                location_user_id: this.locationUserId,
                source: this.source,
            }),
            loading: false,
            valid: false,
        };
    },
    methods: {
        submit() {
            if (!this.$refs.form.validate()) {
                this.$nextTick(() => {
                    const el = document.querySelector(
                        ".v-messages.error--text"
                    );
                    el.scrollIntoView({ block: "center" });
                });

                return;
            }
            this.loading = true;

            this.form.post(route("studentSurvey.store"), {
                preserveState: true,
                preserveScroll: true,
                onSuccess: () => {
                    this.loading = false;
                    this.$emit("success");
                },
                onError: (errors) => {
                    this.loading = false;
                    console.log(
                        "Form returned errors: " + JSON.stringify(errors)
                    );
                    if (errors.contactSupport) return;
                    if (errors.soliError) return;
                },
            });
        },
        reset() {
            this.$refs.form.reset();
        },
    },
};
</script>
